import { Injectable } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  User,
  sendEmailVerification,
  signInWithPopup,
  signOut,
  updateEmail,
} from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { HttpService } from './http.service';
@Injectable({
  providedIn: 'root',
})
export class SocialLoginService {
  user: User | null = null;
  constructor(
    private auth: Auth,
    private httpService: HttpService,
  ) {
    this.auth.onAuthStateChanged((userState) => {
      this.user = userState;
    });
  }

  // Google Login
  async googleSignIn() {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      // provider.setCustomParameters({
      //   prompt: 'select_account', // Ensures the account selection screen is shown
      // });
      const result = await signInWithPopup(this.auth, provider);
      return result.user; // Returns the authenticated user
    } catch (error) {
      console.error('Google login failed:', error);
      return null;
    }
  }

  // Apple Login
  async loginWithApple() {
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(this.auth, provider);
      // Access the idToken from the result
      // const idToken = result._tokenResponse?.idToken;
      // if (idToken) {
      //   // Decode the idToken to retrieve the payload
      //   const decodedToken: any = jwtDecode(idToken);
      //   // Retrieve the original email from the decoded token
      //   const originalEmail = decodedToken.email;
      //   console.log('Original email from Apple Sign-In:', originalEmail);
      //   return { user, originalEmail };  // Return user and original email
      // } else {
      //   console.log('No idToken available');
      //   return null;
      // }

      return result.user; // Returns the authenticated user
    } catch (error) {
      console.error('Apple login failed:', error);
      return null;
    }
  }

  // Microsoft Login
  async loginWithMicrosoft() {
    try {
      const provider = new OAuthProvider('microsoft.com');

      provider.addScope('User.Read');
      provider.addScope('openid');
      provider.addScope('email');
      provider.addScope('profile');
      const result = await signInWithPopup(this.auth, provider);
      // Get the OAuth access token and ID Token
      const credential = OAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;
      const idToken = credential.idToken;

      // Fetch full user profile from Microsoft Graph
      // const userProfile = await this.fetchMicrosoftUserProfile(accessToken);

      return result.user; // Returns the authenticated user
    } catch (error) {
      console.error('loginWithMicrosoft failed:', error);
      return null;
    }
  }

  async fetchMicrosoftUserProfile(accessToken) {
    const response = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const userProfile = await response.json();
    return userProfile;
  }

  // private getProvider(providerId: string): OAuthProvider {
  //   switch (providerId) {
  //     case 'google.com':
  //       return new OAuthProvider('google.com');
  //     case 'facebook.com':
  //       return new OAuthProvider('facebook.com');
  //     case 'microsoft.com':
  //       return new OAuthProvider('microsoft.com');
  //     default:
  //       throw new Error(`Unsupported provider: ${providerId}`);
  //   }
  // }

  // Logout
  async socialLogout(): Promise<void> {
    try {
      await signOut(this.auth);
    } catch (error) {
      console.error('Logout failed socialLogout:', error);
    }
  }

  async updateEmail(email: string): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      try {
        // Update the email address using Firebase's updateEmail method
        await updateEmail(user, email);
        // After email is updated, ensure the user data is refreshed
        await user.reload(); // This reloads the user to get the updated email
      } catch (error) {
        console.error(error);
        throw new Error('Error updating email');
      }
    } else {
      console.error('No user is signed in');
    }
  }

  async sendVerificationEmail(email?: string): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      try {
        // Send verification email to the new email address
        await sendEmailVerification(user);
      } catch (error) {
        console.error('Error sending verification email:', error);
        throw new Error('Error sending verification email');
      }
    } else {
      console.error('No user is signed in');
    }
  }

  async addSSOLoginUser(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_only_user}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Utility function to split a full name into first and last name.
   * @param {string} fullName - The full name to split.
   * @returns {{ firstName: string, lastName: string }} - An object with firstName and lastName.
   */
  splitName(fullName: string): { firstName: string; lastName: string } {
    if (!fullName) {
      return { firstName: '', lastName: '' };
    }

    const [firstName, lastName] = fullName.split(' ').length > 1 ? fullName.split(' ') : [fullName, ''];

    return { firstName: firstName.trim(), lastName: lastName.trim() };
  }
}
